/**
 * Mode State Sync Service - Synchronizes state between text and graphical modes
 * Ensures seamless transitions and data consistency
 */

import { useGameStore } from '@/state/gameStore';
import { locationService } from '@/services/locationService';
import type { PlayerState, StorySegment } from '@/types';

export interface GraphicalPlayerState {
  position: { x: number; y: number };
  location: string;
  karma: number;
  mutations: Array<{ id: string; type?: string; visual?: string }>;
  inventory: Array<{ id: string; name: string; quantity: number }>;
}

export interface TextPlayerState {
  location: string;
  karma: number;
  mutations: Array<{ id: string; type?: string; visual?: string }>;
  inventory: Array<{ id: string; name: string; quantity: number }>;
  storyHistory: StorySegment[];
  currentStorySegment: StorySegment | null;
}

class ModeStateSyncService {
  private lastSyncTime: number = 0;
  private syncThrottle: number = 100; // Sync at most every 100ms

  /**
   * Sync state from graphical mode to text mode
   */
  syncFromGraphical(graphicalState: GraphicalPlayerState): void {
    const now = Date.now();
    if (now - this.lastSyncTime < this.syncThrottle) {
      return; // Throttle syncs
    }
    this.lastSyncTime = now;

    const store = useGameStore.getState();
    const currentPlayerState = store.currentPlayerState;

    if (!currentPlayerState) {
      console.warn('[ModeStateSync] No current player state to sync to');
      return;
    }

    // Update location in location service
    if (graphicalState.location !== locationService.getCurrentLocation()) {
      locationService.switchLocation(
        graphicalState.location,
        graphicalState.position
      );
    }

    // Update Zustand store
    store.setGameState({
      currentPlayerState: {
        ...currentPlayerState,
        location: graphicalState.location,
        // Preserve other state fields
      },
    });

    console.log('[ModeStateSync] Synced from graphical mode:', {
      location: graphicalState.location,
      position: graphicalState.position,
      karma: graphicalState.karma,
    });
  }

  /**
   * Sync state from text mode to graphical mode
   */
  syncFromText(textState: TextPlayerState): GraphicalPlayerState {
    const store = useGameStore.getState();
    const currentPlayerState = store.currentPlayerState;

    // Get location spawn point if switching to graphical mode
    const location = textState.location || currentPlayerState?.location || 'neo-tokyo-plaza';
    const spawnPoint = locationService.getSpawnPoint(location);

    // Update location service if needed
    if (locationService.getCurrentLocation() !== location) {
      locationService.switchLocation(location, spawnPoint);
    }

    const graphicalState: GraphicalPlayerState = {
      position: spawnPoint,
      location: location,
      karma: textState.karma || 0,
      mutations: textState.mutations || [],
      inventory: textState.inventory || [],
    };

    console.log('[ModeStateSync] Synced from text mode:', graphicalState);

    return graphicalState;
  }

  /**
   * Get current state for graphical mode
   */
  getGraphicalState(): GraphicalPlayerState {
    const store = useGameStore.getState();
    const currentPlayerState = store.currentPlayerState;

    const location = currentPlayerState?.location || locationService.getCurrentLocation();
    const spawnPoint = locationService.getSpawnPoint(location);

    return {
      position: spawnPoint,
      location: location,
      karma: 0, // TODO: Get from karma service
      mutations: [], // TODO: Get from mutation service
      inventory: [], // TODO: Get from inventory service
    };
  }

  /**
   * Get current state for text mode
   */
  getTextState(): TextPlayerState {
    const store = useGameStore.getState();
    const currentPlayerState = store.currentPlayerState;

    return {
      location: currentPlayerState?.location || locationService.getCurrentLocation(),
      karma: 0, // TODO: Get from karma service
      mutations: [], // TODO: Get from mutation service
      inventory: [], // TODO: Get from inventory service
      storyHistory: currentPlayerState?.storyHistory || [],
      currentStorySegment: store.currentStorySegment || currentPlayerState?.currentStorySegment || null,
    };
  }

  /**
   * Save state before mode switch
   */
  saveStateBeforeSwitch(): void {
    const store = useGameStore.getState();
    const currentPlayerState = store.currentPlayerState;

    if (currentPlayerState) {
      // State is already in Zustand store, which persists
      console.log('[ModeStateSync] State saved before mode switch');
    }
  }

  /**
   * Restore state after mode switch
   */
  restoreStateAfterSwitch(): void {
    const store = useGameStore.getState();
    const currentPlayerState = store.currentPlayerState;

    if (currentPlayerState) {
      // Restore location
      const location = currentPlayerState.location || locationService.getCurrentLocation();
      if (locationService.getCurrentLocation() !== location) {
        locationService.switchLocation(location, locationService.getSpawnPoint(location));
      }

      console.log('[ModeStateSync] State restored after mode switch:', {
        location: location,
      });
    }
  }
}

export const modeStateSyncService = new ModeStateSyncService();

